<template>
  <div class="visualizer-container">
    <v-container class="d-flex flex-column container" fluid>
      <v-row class="top-row">
        <v-col class="d-flex align-center" cols="auto">
          <BackButton class="backButton my-auto" style="color: #197F9F !important;"/>
          <w-tooltip bottom :nudge-bottom="4" :max-width="250">
            <template v-slot:activator>
              <div class="callFlowHeading my-auto">
                {{ l.t('visualizer.visual-call-flow', 'Visual Call Flow:') }}
              </div>
            </template>
            {{ l.t('visualizer.double-click-to-open-items-modal', 'Double click to open item’s modal.') }}
            <br>
            {{ l.t('visualizer.click-and-hold-on-an-item-to-move-it', 'Click and hold on an item to move it.') }}
            <br>
            {{ l.t('visualizer.click-and-hold-on-the-background', 'Click and hold on the background to move the whole visual.') }}
            <br>
            {{ l.t('visualizer.scroll-up-and-down-to-zoom-in-and-out', 'Scroll up and down to zoom in and out.') }}
          </w-tooltip>
          <div class="show-more ml-2" @click="show_more = !show_more">
            <v-icon>{{ showMoreIcon }}</v-icon>
          </div>
        </v-col>
        <template v-if="show_more">
          <v-col cols="auto" class="object-type center-autocomplete">
            <w-select
              v-model="filterType"
              :items="['Phone numbers', 'Extensions/Users']"
              item-text="name"
              class="mb-2"
              hide-details="auto"
              return-object
            />
          </v-col>
          <v-col cols="auto" class="center-autocomplete" :key="!!chosenItem">
            <APIAutocomplete
              v-if="filterType === 'Extensions/Users'"
              v-model="chosenItem"
              :return_object="true"
              :no_update="true"
              mode="extensions"
              key="extensions"
              data-test-id="visualizer-extension-selector"
            />
            <APIAutocomplete
              v-else-if="filterType === 'Phone numbers'"
              v-model="chosenItem"
              :return_object="true"
              :no_update="true"
              mode="phone_numbers"
              data-test-id="visualizer-number-selector"
            />
          </v-col>
        </template>
      </v-row>
      <VisualizerController
        v-if="toShow && !loading"
        @zoomScene="zoomBlocks"
        @centerScene="centerBlocks"
        @moveScene="moveBlocks"
      />
      <BlocksContainer
        v-if="toShow && !loading"
        ref="container"
        :blocksContent="blocks"
        :scene.sync="scene"
        :maxHeight="maxHeight"
        :maxWidth="maxWidth"
        @openModal="openModal"
        class="block-container"
      />
    </v-container>
    <w-loader v-if="loading"/>
    <DefaultModal
      v-if="modal_type && modal_type === 'device'"
      v-model="show_modal"
      :value="true"
      @click:cancel="show_modal = false"
      @close="modal_type = null"
      :width="500"
      data-test-id="modal-with-trigger"
    >
      <template v-slot:title>
        {{sip_can_be_visible(currentDevice.code, currentDevice.type) ? l.t('devices.device-details-sip', 'Device details/SIP') : l.t('devices.device-details', 'Device details')}}
      </template>
      <VerticalTable
        :items="details_and_sip(currentDevice)"
      >
      </VerticalTable>
      <template v-slot:buttons>
        <w-btn color="secondary" @click="show_modal = false">{{l.t('app.cancel', 'Cancel')}}</w-btn>
        <w-btn text>
          <CopyToClipboard
            :copy="text_to_be_copied(currentDevice)"
            :data-test-id="`devices-item-${currentDevice.id}-copy-to-clipboard`"
          />
        </w-btn>
      </template>
    </DefaultModal>
    <FormModal
      v-else-if="modal_type && modal_type !== 'device'"
      v-model="show_modal"
      :with_x="true"
      @close="modal_type = null"
      :width="modal_type === 'trunk' ? 750 : 1000"
    >
      <MultiResourceModalContent
        v-if="modal_type === 'extension'"
        :id="actionId"
        type="extension"
        @updated="update_screen"
        @click:cancel="show_modal = false"
        data-test-id="autocomplete-edit-extension"
      />
      <MultiResourceModalContent
        v-if="modal_type === 'preset'"
        :id="actionId"
        type="route"
        @updated="update_screen"
        @click:cancel="show_modal = false"
        data-test-id="autocomplete-edit-route"
      />
      <MultiResourceModalContent
        v-if="modal_type === 'phone_number'"
        :id="actionId"
        type="phone_number"
        @updated="update_screen"
        @click:cancel="show_modal = false"
        data-test-id="autocomplete-edit-phone-num"
      />
      <Menu
        v-if="modal_type === 'menu'"
        :id="actionId"
        @click:cancel="show_modal = false"
        :full_menu="true"
        @updated="update_screen"
        data-test-id="selector-edit-menu" />
      <Trunk
        v-if="modal_type === 'trunk'"
        :id="actionId"
        @click:cancel="show_modal = false"
        @updated="update_screen"
        data-test-id="selector-edit-trunk">
      </Trunk>
      <Queue
        v-if="modal_type === 'queue'"
        :id="actionId"
        @click:cancel="show_modal = false"
        @updated="update_screen"
        data-test-id="selector-edit-queue"/>
      <LiveReceptionistShow
        v-if="modal_type === 'receptionist'"
        :_id="actionId"
        @click:cancel="show_modal = false"
        @updated="update_screen"
        data-test-id="selector-edit-lr"/>
    </FormModal>
  </div>
</template>

<script>
  import l from '../../../libs/lang';
  import { is_csr } from '../../../libs/helpers';
  import { formatPhoneNumber } from '../../../libs/formatters';
  import LiveReceptionistShow from '../live-answer/show.vue';
  import BlocksContainer from './components/BlocksContainer.vue';
  import VisualizerController from './components/VisualizerController.vue';
  import FormInput from '../../elements/form/FormInput.vue';
  import Visualizer from './helpers/visualizer-helpers';
  import VerticalTable from '../../elements/VerticalTable.vue';
  import CopyToClipboard from '../../elements/CopyToClipboard.vue';
  import DefaultModal from '../../elements/modal/DefaultModal.vue';
  import FormModal from '../../elements/modal/FormModal.vue';
  import BackButton from '../../elements/BackButton.vue';
  import APIAutocomplete from '../../elements/form/APIAutocomplete.vue';

  export default {
    name: 'visualizerApp',
    components: {
      FormInput,
      BackButton,
      FormModal,
      DefaultModal,
      VerticalTable,
      LiveReceptionistShow,
      APIAutocomplete,
      CopyToClipboard,
      BlocksContainer,
      VisualizerController,
      Menu: () => import('../menus/show.vue'),
      Trunk: () => import('../trunks/show.vue'),
      Queue: () => import('../queues/show.vue'),
      MultiResourceModalContent: () => import('../../elements/MultiResourceModalContent.vue'),
    },
    mounted() {
      const element = document.getElementById('page-content');
      if (element) {
        element.classList.add('visualizer-content');
      }
      window.addEventListener('resize', () => {
        this.show_more = this.show_more || this.calculateShowMore();
      });
    },
    destroyed() {
      const element = document.getElementById('page-content');
      if (element) {
        element.classList.remove('visualizer-content');
      }
      window.removeEventListener('resize', () => { });
    },
    data() {
      return {
        l,
        show_modal: false,
        codes_that_can_see_sip: [17349, 17390, 17392, 17393, 17394, 17396, 17397],
        is_csr: false,
        actionId: -1,
        loading: false,
        isStaggered: false,
        modal_type: null,
        filterType: this.$route.name.includes('phone-numbers') ? 'Phone numbers' : 'Extensions/Users',
        toShow: false,
        show_more: true,
        menuCallFlows: [],
        scripts: [],
        routes: [],
        blocks: [],
        menus: [],
        queues: [],
        devices: [],
        call_flow_counter: 0,
        all_extensions: [],
        extensions: [],
        phone_numbers: [],
        actionIds: {},
        chosenItem: null,
        currentDevice: null,
        callFlowBlockCounter: 1,
        startingX: 0,
        distanceBetween: 335,
        maxHeight: 0,
        newIDs: 1,
        maxWidth: 250,
        scene: {
          blocks: [],
          links: [],
        },
      };
    },
    async created() {
      this.loading = true;
      this.is_csr = await is_csr(this.$session);
      if (this.$route.params.id && this.$route.name === 'phone-numbers.visualize') {
        this.chosenItem = await this.$session.get_item(`/phone-numbers/${this.$route.params.id}`);
      } else if (this.$route.params.id && this.$route.name === 'extensions.visualize') {
        this.chosenItem = await this.$session.get_item(`/extensions/${this.$route.params.id}`);
      }
      this.loading = false;
    },
    computed: {
      showMoreIcon() {
        return this.show_more ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down';
      },
    },
    methods: {
      calculateShowMore() {
        return document.documentElement.clientWidth > 680;
      },
      sip_can_be_visible(code, type) {
        return this.$data.is_csr || this.codes_that_can_see_sip.includes(code) || type === 'softphone';
      },
      device_name(item) {
        if (item.model) {
          return `${item.model.manufacturer} ${item.model.name}`;
        }
        if (item.type === 'generic') return l.t('device.generic-device', 'Generic device');

        return '—';
      },
      details_and_sip(item) {
        let items = [
          {text: l.t('app.id', 'ID'), value: item.id},
          {text: l.t('app.device-name', 'Device name'), value: this.device_name(item)},
          {text: l.t('app.mac-address', 'MAC address'), value: item.mac_address || '–'},
          {text: l.t('app.serial-number', 'Serial number'), value: item.serial_number || '–'},
        ];
        for (const line of item.lines) {
          items.push({
            text: `${l.t('app.line', 'Line')} ${line.line}`,
            value: `${l.t('app.extension', 'Extension')} ${line.extension.extension}`,
          });
        }
        if (this.sip_can_be_visible(item.code, item.type)) {
          items = items.concat([
            {text: l.t('devices.sip-proxy', 'SIP proxy'), value: item.sip_authentication.host},
            {text: l.t('app.port', 'Port'), value: item.sip_authentication.port},
            {text: l.t('app.username', 'Username'), value: item.sip_authentication.username},
            {text: l.t('app.password', 'Password'), value: item.sip_authentication.password},
          ]);
        }

        return items;
      },
      text_to_be_copied(device) {
        const text = [];
        const details = this.details_and_sip(device);
        for (const detail of details) {
          text.push(`${detail.text}: ${detail.value}`);
        }
        return text.join('\n');
      },
      zoomBlocks(val) {
        this.$refs.container.buttonScroll(val);
      },
      centerBlocks() {
        this.$refs.container.center();
      },
      moveBlocks(val) {
        this.$refs.container.moveScene(val.axis, val.amount);
      },
      async update_screen(data) {
        setTimeout(() => {
          this.modal_type = null;
          this.show_modal = false;
          this.loading = true;
        }, 1000);
        const mapping = [
          { key: 'phone_number', list: 'phone_numbers', endpoint: 'phone-numbers' },
          { key: 'extension', list: 'extensions', endpoint: 'extensions' },
          { key: 'members', list: 'queues', endpoint: 'queues' },
        ];

        if (data) {
          const map = mapping.find((m) => data[m.key]);
          if (map) {
            const foundIndex = this[map.list].findIndex((item) => item.id === data.id);
            if (foundIndex !== -1) {
              this[map.list][foundIndex] = await this.$session.get_item(
                `/${map.endpoint}/${data.id}`
              );
            }
          }
        } else {
          Object.assign(this, {
            menus: [],
            routes: [],
            scripts: [],
            devices: [],
          });
        }

        const type = this.chosenItem.phone_number ? 'phone-numbers' : 'extensions';
        this.chosenItem = await this.$session.get_item(`/${type}/${this.chosenItem.id}`);
      },
      async changeMapOption() {
        try {
          Object.assign(this, {
            scene: { blocks: [], links: [] },
            blocks: [],
            menuCallFlows: [],
            call_flow_counter: 0,
            callFlowBlockCounter: 1,
            actionIds: {},
            newIDs: 1,
            maxHeight: 0,
            maxWidth: 250,
            isStaggered: false,
            toShow: false,
            loading: true,
            show_more: this.calculateShowMore(),
          });

          await this.startRoute(this.chosenItem);

          this.loading = false;
          this.toShow = true;
        } catch (error) {
          console.warn(error);
          this.loading = false;
        }
      },
      async startRoute(response) {
        if (response.phone_number) {
          this.addPhoneNumberBlock(response);
        } else {
          this.addExtensionBlock(response);
        }

        await this.loopBlocks(this.startingX, 1);
        this.staggerBlocksv2(this.scene.blocks[0]);
        if (this.isStaggered) {
          this.scene.blocks.sort((a, b) => a.newID - b.newID);
        }
        this.alignObjects(this.startingX);
      },
      async loopBlocks(amount, sceLen) {
        const sceneBLocks = this.scene.blocks.filter((block) => block.x === amount);
        for (const sceneBlock of sceneBLocks) {
          const block = this.blocks[sceneBlock.id - 1];
          const outputs = block.fields.filter((item) => item.type);
          await this.configureBlocks(outputs, sceLen, sceneBlock);
        }
        if (sceneBLocks.length) {
          await this.loopBlocks(amount + this.distanceBetween, sceLen + 1);
        }
      },
      alignObjects(amount) {
        if (this.scene.blocks.length) {
          const blocks = this.scene.blocks.filter((b) => b.x === amount);
          if (blocks.length) {
            const total = blocks.reduce((a, c) => a + (c.height || 0), 0);
            let newY = (total / 2) * (-1);

            for (const block of blocks) {
              block.y = newY;
              newY += block.height;
            }

            this.maxHeight = total > this.maxHeight ? total : this.maxHeight;
            return this.alignObjects(amount + this.distanceBetween);
          }
          this.maxWidth = (this.scene.blocks.length > 1) ? amount : 250;
        }
        return true;
      },
      staggerBlocksv2(block) {
        if (!block?.newID) {
          const outputs = this.blocks[block.id - 1].fields.filter((item) => item.type);
          block.newID = this.newIDs++;
          for (const output of outputs) {
            if (output.type === 'call flow') {
              this.call_flow_counter++;
              this.staggerBlocksv2(this.scene.blocks.find((b) => b.name === `Call Flow ${this.call_flow_counter}`));
            } else {
              this.staggerBlocksv2(this.scene.blocks[this.actionIds[output.actionId]]);
            }
          }
          this.alignBlocksv2(block);
        }
      },
      alignBlocksv2(lastSceneBlock) {
        const flagLinks = this.scene.links.filter((link) => link.originID === lastSceneBlock.id && !link.backlink);
        const seenTargetIds = {};
        const links = flagLinks.filter((link) => {
          if (!seenTargetIds[link.targetID]) {
            seenTargetIds[link.targetID] = true;
            return true;
          }
          return false;
        });
        const blocksHeight = links.reduce((a, c) => a + (c.targetHeight || 0), 0);
        const alignBlocks = this.scene.blocks.filter((b) => links.map((link) => link.targetID).includes(b.id));

        if (alignBlocks.length > 4 && lastSceneBlock.height < blocksHeight) {
          this.isStaggered = true;
          let i = 0;
          for (const block of alignBlocks) {
            if (i % 2 !== 0) {
              block.x += this.distanceBetween;
            }
            i++;
          }

          for (const block of this.scene.blocks.filter((b) => b.newID > lastSceneBlock.newID && !links.find((li) => li.targetID === b.id) && b.x > lastSceneBlock.x)) {
            block.x += this.distanceBetween;
          }
        }
      },
      async configureBlocks(outputs, sceLen, block) {
        const typeHandlers = {
          extension: async (output) => {
            const item = await this.getItem('extensions', 'extensions', output.actionId);
            if (item) this.addExtensionBlock(item, sceLen, output.id, block);
          },
          menu: async (output) => {
            const item = await this.getItem('menus', 'menus', output.actionId);
            if (item) this.addMenu(item, sceLen, output.id, block);
          },
          receptionist: async (output) => {
            const item = await this.getItem('scripts', 'live-answer', output.actionId);
            if (item) await this.addReceptionistBlock(item, sceLen, output.id, block);
          },
          device: async (output) => {
            const item = await this.getItem('devices', 'devices', output.actionId);
            if (item) this.addDeviceBlock(item, sceLen, output.id, block);
          },
          trunk: async (output) => {
            this.addTrunkBlock({ name: output.actionName, id: output.actionId }, sceLen, output.id, block);
          },
          queue: async (output) => {
            const item = await this.getItem('queues', 'queues', output.actionId);
            if (item) this.addQueueBlock(item, sceLen, output.id, block);
          },
          preset: async (output) => {
            const item = await this.getItem('routes', 'routes', output.actionId);
            if (item) this.addPresetBlock(item, sceLen, output.id, block);
          },
          directory: async (output) => {
            await this.addDirectory(sceLen, output.id, block);
          },
          'call flow': async (output) => {
            const call_flow = this.menuCallFlows.find((cf) => cf.name === output.name);
            if (call_flow) this.addCallFlowBlock(call_flow, sceLen, output.id, block);
          }
        };

        for (const output of outputs) {
          const found = (output.type !== 'call flow' && this.actionIds[output.actionId] !== undefined)
            ? this.scene.blocks[this.actionIds[output.actionId]]
            : null;

          if (found) {
            this.addSceneLink(block, output.id, found);
          } else if (typeHandlers[output.type]) {
            await typeHandlers[output.type](output);
          }
        }
      },
      async getItem(collection, endpoint, id) {
        try {
          let item = this[collection].find((d) => d.id === id);
          if (collection === 'extensions' && this.all_extensions.length) {
            item = this.all_extensions.find((d) => d.id === id);
            if (item) this[collection].push(item);
          }
          if (!item) {
            item = await this.$session.get_item(`/${endpoint}/${id}`);
            this[collection].push(item);
          }
          return item;
        } catch (e) {
          console.error(e);
          return null;
        }
      },
      getDeviceType(type) {
        switch (type) {
          case 'desk':
            return 'Desk phone';
          case 'iphone':
            return 'iPhone';
          case 'generic':
            return 'Generic device';
          default:
            return type;
        }
      },
      addExtensionBlock(extension, sceLen = 0, outputID = null, lastBlock = null) {
        const options = {
          title: `Extension/User ${extension.extension}`,
          name: extension.name,
          actionId: extension.id,
          color: 'rgba(244, 245, 246, 0.8)',
          objectType: 'extension',
          fields: Visualizer.getBlockOutputs(extension)
        };
        const device_info = {
          esim: extension.esim,
          device_membership: extension.device_membership,
          role: extension.role,
        };
        this.addBlocks(options, sceLen, outputID, lastBlock, device_info);
      },
      addPresetBlock(preset, sceLen, outputID = null, lastBlock = null) {
        const options = {
          title: 'Preset',
          name: preset.name,
          actionId: preset.id,
          color: 'rgba(216, 251, 201, 0.8)',
          objectType: 'preset',
          fields: Visualizer.getBlockOutputs({route: preset})
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      addPhoneNumberBlock(phone_number, sceLen = 0, outputID = null, lastBlock = null) {
        const options = {
          title: `Phone Number: ${this.format_phone_number(phone_number.phone_number)}`,
          name: phone_number.name,
          actionId: phone_number.id,
          color: 'rgba(247, 255, 229, 0.8)',
          objectType: 'phone_number',
          fields: Visualizer.getBlockOutputs(phone_number)
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      addDeviceBlock(device, sceLen, outputID, lastBlock) {
        const title = device.esim ? 'ProSIM' : this.getDeviceType((device.model && device.model.type) ? device.model.type : device.type);
        let name = device.name || '';
        if (device.model && device.model.name && device.model.manufacturer) {
          name = `${device.model.manufacturer} ${device.model.name}`;
        }
        const options = {
          title,
          name,
          actionId: device.id,
          color: 'rgba(231, 235, 255, 0.8)',
          objectType: 'device',
          fields: Visualizer.getDeviceOutputs(title)
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      addTrunkBlock(trunk, sceLen, outputID, lastBlock) {
        const options = {
          title: 'Trunk',
          name: trunk.name,
          actionId: trunk.id,
          color: 'rgba(231, 235, 255, 0.8)',
          objectType: 'trunk',
          fields: Visualizer.getDeviceOutputs('Trunk')
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      async addReceptionistBlock(script, sceLen, outputID, lastBlock) {
        const fields = Visualizer.getReceptionistOutputs(script);
        await this.updateReceptionistFields(fields);
        const options = {
          title: 'Receptionist services',
          name: script.name,
          actionId: script.id,
          color: 'rgba(247, 237, 255, 0.8)',
          objectType: 'receptionist',
          fields
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      addMenu(menu, sceLen, outputID, lastBlock) {
        const menuOutputs = Visualizer.getMenuOutputs(menu, this.callFlowBlockCounter);
        this.callFlowBlockCounter = menuOutputs.counter;
        this.menuCallFlows.push(...menuOutputs.menuCallFlows);
        const options = {
          title: 'Menu',
          name: menu.name,
          actionId: menu.id,
          color: 'rgba(234, 245, 248, 0.8)',
          objectType: 'menu',
          fields: menuOutputs.fields
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      async addDirectory(sceLen, outputID, lastBlock) {
        const response = await this.$session.get_list_all('/extensions');
        this.all_extensions = response.items;
        const eligible_extensions = this.all_extensions.filter((ext) => ext.include_in_directory);
        const fields = Visualizer.getDirectoryOutputs(eligible_extensions);
        const options = {
          title: 'Dial-by-name directory',
          name: 'Directory',
          actionId: null,
          color: 'rgba(212, 247, 239, 0.8)',
          objectType: 'directory',
          fields: fields.length <= 15 ? fields : Visualizer.getDeviceOutputs('full-directory')
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      addQueueBlock(queue, sceLen, outputID, lastBlock) {
        const options = {
          title: 'Queue',
          name: queue.name,
          actionId: queue.id,
          color: 'rgba(247, 235, 212, 0.8)',
          objectType: 'queue',
          fields: Visualizer.getQueueOutputs(queue)
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      addCallFlowBlock(call_flow, sceLen, outputID, lastBlock) {
        const options = {
          title: call_flow.name,
          name: call_flow.name,
          actionId: -1,
          color: 'rgba(253, 237, 235, 0.8)',
          objectType: 'call flow',
          fields: call_flow.fields
        };
        this.addBlocks(options, sceLen, outputID, lastBlock);
      },
      async updateReceptionistFields(fields) {
        for (const field of fields.filter((f) => f.actionId)) {
          const extension = await this.getItem('extensions', 'extensions', field.actionId);
          field.actionName = extension ? extension.name : '';
          if (!extension) {
            field.actionId = null;
            field.type = null;
          }
        }
      },
      addBlocks(options, sceLen, outputID, lastBlock, device_info = null) {
        this.addFirstBlock(options, device_info);
        this.addSceneBlock(options, sceLen);
        if (outputID !== null) this.addSceneLink(lastBlock, outputID, this.scene.blocks[this.scene.blocks.length - 1]);
      },
      addFirstBlock(options, device_info) {
        if (this.blocks.length) options.fields.push({name: '', attr: 'input'});

        if ((device_info?.device_membership || device_info?.esim) && options.fields.find((field) => field.actionId === options.actionId)) {
          const device_outputs = options.fields.filter((field) => field.actionId === options.actionId && field.actionType === 'Forward call');
          for (const field of device_outputs) {
            field.actionName = 'Ring my device';
            field.device = device_info?.esim ? 'esim' : device_info.role;
            field.actionId = device_info?.esim ? device_info.esim.voip_device_id : device_info.device_membership.device.id;
            field.type = 'device';
          }
        }

        this.blocks.push({...options});
      },
      addSceneBlock(options, sceLen) {
        this.actionIds[options.actionId] = this.scene.blocks.length;
        const { fields, ...rest } = options;

        this.scene.blocks.push({
          id: this.scene.blocks.length + 1,
          ...rest,
          x: this.startingX + sceLen * this.distanceBetween,
          y: this.scene.blocks.length + 1,
          height: fields.length * 21 + (options.objectType === 'call flow' ? 30 : 60),
        });
      },
      addSceneLink(originBlock, originSlot, targetBlock) {
        this.scene.links.push({
          id: this.scene.links.length + 1,
          originID: originBlock.id,
          backlink: targetBlock.x < originBlock.x + 230,
          targetID: targetBlock.id,
          targetSlot: 0,
          originSlot: parseInt(originSlot),
          targetHeight: targetBlock.height,
        });
      },
      format_phone_number(number) {
        if (!number) return '';
        return formatPhoneNumber(number);
      },
      openModal(event) {
        if (event.actionId) {
          if (event.modal_type === 'device') {
            this.currentDevice = this.devices.find((device) => device.id === event.actionId);
          }
          this.actionId = event.actionId;
          this.modal_type = event.modal_type;
          this.show_modal = true;
        }
      },
    },
    watch: {
      chosenItem() {
        this.changeMapOption(this.chosenItem.phone_number ? 'phone_number' : 'extension');
      },
    }
  };
</script>

<style lang='scss' scoped>
.visualizer-container {
  height: 100%;
  margin-top: -15px;
  margin-left: 28px;
}
.visualizer-container::v-deep .v-input__slot {
  background-color: white !important;
}
.block-container {
  position: fixed;
  top: 65px;
  left: 222px;
  height: 100%;
  padding-left: 0px;
  width: 100%;
  flex-grow: 1;
  margin-top: -8px;
  @media only screen and (max-width: 961px) {
    left: 0px;
  }
}
.callFlowHeading {
  font-weight: 600;
  margin-top: 5px;
  font-size: 10px;
  display: flex;
  align-items: center;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #197F9F;
}
.callFlowCol {
  display: flex;
  align-items: center;
}
.top-row {
  flex-shrink: 0;
  background-color: white;
  width: max-content;
  z-index: 2;
  border-radius: 9px;
  box-shadow: 0 0px 1px 0 rgba(0, 0, 0, 0.15), 0 1px 5px 0 rgba(0, 0, 0, 0.15);
}
.object-type {
  max-width: 200px;
}
.center-autocomplete {
  margin-top: -6px;
}
.dynamic-width-autocomplete {
  width: auto !important;
}
.chosenItemCol {
  width: 360px;
}
.backButton {
  margin-top: 0px;
  font-weight: 600 !important;
  font-size: 10px !important;
  display: flex;
  align-items: center;
  letter-spacing: 0.4px !important;
  text-transform: uppercase !important;
  color: #197F9F !important;
}
.show-more {
  display: none;
}
@media only screen and (max-width: 680px) {
  .top-row {
    display: flex;
    flex-direction: column;
  }
  .show-more {
    display: block;
  }
  .visualizer-container {
    margin-top: -25px;
    margin-left: -65px;
  }
}
@media only screen and (max-width: 1400px) {
  .visualizer-container {
    margin-left: -19px;
  }
}
@media only screen and (max-width: 959px) {
  .visualizer-container {
    margin-left: -35px;
    margin-top: -25px;
  }
}
@media only screen and (max-width: 480px) {
  .visualizer-container {
    margin-left: 0px;
  }
}
</style>
